import { Component, Output, EventEmitter, Input, OnInit, OnDestroy } from '@angular/core';
import { EditOrganizationService } from './edit-organization.service';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from 'src/app/core/services/auth.service';
import { MatCardModule } from '@angular/material/card';
import { Router, ActivatedRoute } from '@angular/router';
import { OrganizationStateService } from '../../../shared/organization-state.service';

@Component({
  selector: 'app-edit-organization',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatCardModule],
  templateUrl: './edit-organization.component.html',
  styleUrl: './edit-organization.component.scss',
})
export class EditOrganizationComponent implements OnInit, OnDestroy {
  editForm: FormGroup;
  organizationId: string = '';
  organizationData: any = {};
  originalActiveState: boolean = false;
  originalFormValue: any = {};
  hasFormChanges: boolean = false;
  types = [];
  serverError: any;
  updateSuccess = false;
  isLoading = false;
  private _alertTimeout: any;

  @Input() organizationToEdit: any;
  @Output() editOrgSuccess = new EventEmitter<void>();

  constructor(
    private fb: FormBuilder,
    private editOrganizationService: EditOrganizationService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private organizationStateService: OrganizationStateService
  ) {
    this.editForm = this.fb.group({
      companyType: ['', [Validators.required]], // Use same field name as add customer
      name: ['', [Validators.required]],
      displayName: [''],
      country: ['USA'],
      soldTo: [''],
      shipTo: [''],
      excludeReferrals: [false],
      active: [false],
      logo: [''],
      // Address fields
      address1: [''],
      address2: [''],
      city: [''],
      state: [''],
      postalCode: [''],
      phone: [''],
      // Contact info fields
      firstName: [''],
      lastName: [''],
      pointOfContactDetails: ['']
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.organizationId = params.get('id') || '';
      if (this.organizationId) {
        this.loadOrganizationData();
        this.loadOrganizationTypes();
      }
    });

    // Subscribe to form value changes to track if changes are made
    this.editForm.valueChanges.subscribe(() => {
      this.checkForFormChanges();
    });
  }

  loadOrganizationData() {
    this.isLoading = true;
    const token = this.authService.token;
    this.editOrganizationService.getOrganization(this.organizationId, token).subscribe({
      next: (data) => {
        this.organizationData = data;
        this.populateForm(data);
        this.isLoading = false;
      },
      error: (error) => {
        this.serverError = error;
        this.isLoading = false;
      }
    });
  }

  loadOrganizationTypes() {
    const token = this.authService.token;
    this.editOrganizationService.getOrganizationTypes(token).subscribe({
      next: (data) => {
        this.types = data.organizationTypes;
      },
      error: (err) => {
        console.error('Failed to load organization types:', err);
      }
    });
  }

  populateForm(data: any) {
    this.originalActiveState = data.active ?? false;
    const formData = {
      ...this.extractBasicFields(data),
      ...this.extractAddressFields(data),
      ...this.extractContactFields(data)
    };
    
    this.editForm.patchValue(formData);
    
    // Store original form values for comparison
    this.originalFormValue = { ...formData };
    this.hasFormChanges = false;
  }

  private extractBasicFields(data: any) {
    return {
      ...this.extractCoreOrgFields(data),
      ...this.extractBusinessFields(data),
      ...this.extractConfigFields(data)
    };
  }

  private extractCoreOrgFields(data: any) {
    return {
      companyType: data.organizationTypeId || '',
      name: data.name || '',
      displayName: data.displayName || '',
      country: data.address?.country || 'USA'
    };
  }

  private extractBusinessFields(data: any) {
    return {
      soldTo: data.soldTo || '',
      shipTo: data.shipTo || '',
      logo: data.logoObjectKey || ''
    };
  }

  private extractConfigFields(data: any) {
    return {
      excludeReferrals: data.excludeReferrals || false,
      active: data.active ?? false
    };
  }

  private extractAddressFields(data: any) {
    const address = data.address || {};
    return {
      address1: address.address1 || '',
      address2: address.address2 || '',
      city: address.city || '',
      state: address.state || '',
      postalCode: address.postalCode || '',
      phone: address.phone || ''
    };
  }

  private extractContactFields(data: any) {
    const contactInfo = data.contactInfo || {};
    return {
      firstName: contactInfo.firstName || '',
      lastName: contactInfo.lastName || '',
      pointOfContactDetails: contactInfo.pointOfContactDetails || ''
    };
  }

  checkForFormChanges() {
    if (!this.originalFormValue || Object.keys(this.originalFormValue).length === 0) {
      this.hasFormChanges = false;
      return;
    }

    const currentFormValue = this.editForm.value;
    
    // Compare current form values with original values
    this.hasFormChanges = Object.keys(this.originalFormValue).some(key => {
      const originalValue = this.originalFormValue[key];
      const currentValue = currentFormValue[key];
      
      // Handle string comparison (trim whitespace)
      if (typeof originalValue === 'string' && typeof currentValue === 'string') {
        return originalValue.trim() !== currentValue.trim();
      }
      
      // Handle other types (boolean, number, etc.)
      return originalValue !== currentValue;
    });
  }

  onSubmit() {
    // Mark all controls as touched to trigger validation messages
    this.editForm.markAllAsTouched();

    if (this.editForm.invalid) {
      return;
    }

    const formValue = this.editForm.value;
    const updateData = this.buildUpdateData(formValue);
    this.processOrganizationUpdate(updateData, formValue);
  }

  private buildUpdateData(formValue: any) {
    const organizationTypeId = this.normalizeOrganizationTypeId(formValue.companyType);
    
    return {
      orgId: this.organizationId,
      name: formValue.name,
      displayName: formValue.displayName,
      address: this.buildAddressData(formValue),
      contactInfo: this.buildContactData(formValue),
      organizationTypeId: organizationTypeId,
      shipTo: formValue.shipTo,
      soldTo: formValue.soldTo,
      clientCertificateVerificationRequired: false,
      excludeReferrals: formValue.excludeReferrals,
      locale: "string",
      logoObjectKey: formValue.logo || null
    };
  }

  private normalizeOrganizationTypeId(companyType: any): string {
    if (companyType && typeof companyType !== 'string') {
      return companyType.toString();
    }
    return companyType;
  }

  private buildAddressData(formValue: any) {
    return {
      address1: formValue.address1 || '',
      address2: formValue.address2 || '',
      country: formValue.country || 'USA',
      city: formValue.city || '',
      state: formValue.state || '',
      postalCode: formValue.postalCode || '',
      phone: formValue.phone || ''
    };
  }

  private buildContactData(formValue: any) {
    return {
      firstName: formValue.firstName || '',
      lastName: formValue.lastName || '',
      pointOfContactDetails: formValue.pointOfContactDetails || ''
    };
  }

  private processOrganizationUpdate(updateData: any, formValue: any) {
    this.isLoading = true;
    const token = this.authService.token;
    
    // First update the organization data
    this.editOrganizationService.updateOrganization(updateData, token).subscribe({
      next: (response) => {
        this.handleUpdateSuccess(response, formValue);
      },
      error: (error) => {
        this.handleUpdateError(error);
      }
    });
  }

  private handleUpdateSuccess(response: any, formValue: any) {
    const currentActiveState = formValue.active;
    if (currentActiveState !== this.originalActiveState) {
      this.handleActiveStatusChange(currentActiveState, response);
    } else {
      this.completeSuccessfulUpdate(response);
    }
  }

  private handleActiveStatusChange(currentActiveState: boolean, response: any) {
    const token = this.authService.token;
    const activeOperation = currentActiveState 
      ? this.editOrganizationService.activateOrganization(this.organizationId, token)
      : this.editOrganizationService.deactivateOrganization(this.organizationId, token);
    
    activeOperation.subscribe({
      next: (activeResponse) => {
        this.completeSuccessfulUpdate(response);
      },
      error: (error) => {
        this.handleUpdateError(error);
      }
    });
  }

  private completeSuccessfulUpdate(response: any) {
    this.isLoading = false;
    this.serverError = null;
    this.organizationStateService.setOrgResponse(response.data);
    this.editOrgSuccess.emit();
    
    // Navigate to show-organization with success message
    this.router.navigate(['/organization/show-organization', this.organizationId], {
      queryParams: { updated: '1' }
    });
  }

  private handleUpdateError(error: any) {
    this.serverError = error;
    this.updateSuccess = false;
    this.isLoading = false;
    
    // Clear error message after 4 seconds
    this._alertTimeout = setTimeout(() => {
      this.serverError = null;
    }, 4000);
  }

  onCancel() {
    this.router.navigate(['/organization/show-organization', this.organizationId]);
  }

  dismissAlert() {
    this.updateSuccess = false;
    this.serverError = null;
    if (this._alertTimeout) {
      clearTimeout(this._alertTimeout);
      this._alertTimeout = null;
    }
  }

  ngOnDestroy() {
    if (this._alertTimeout) {
      clearTimeout(this._alertTimeout);
    }
  }
}